<template>
  <div class="select-wrapper">
    <BaseDropdown
      ref="dropdownRef"
      class="select show-from-desktop"
      :style="{
        width: width
      }"
      :trigger-style="{
        width: '100%'
      }"
      :content-style="{
        ...dropdownStyle,
      }"
      :content-wrapper-style="{
        width: dropdownWidth
      }"
    >
      <template #trigger="{ toggleShow, isShownDropdown }">
        <template v-if="slots['trigger']">
          <slot
            name="trigger"
            :placeholder-string="placeholderString"
            :selected-option="selectedOption"
          />
        </template>

        <BaseSelectTrigger
          :placeholder="placeholderString"
          :has-selected="hasSelected"
          :is-opened="isShownDropdown"
          :label="label"
          :theme="triggerTheme"
          :error="error"
          @click="toggleShow"
        >
          <template v-if="slots['trigger-content']" #base-trigger-content>
            <slot
              name="trigger-content"
              :placeholder-string="placeholderString"
              :selected-option="selectedOption"
            />
          </template>
        </BaseSelectTrigger>
      </template>

      <template #content="{ hide }">
        <template v-if="search">
          <div class="select-search">
            <BaseInput v-model="searchValue" size="small" :placeholder="$t('common.select_search')" />
          </div>
        </template>
        <template v-if="includeDefaultOption">
          <BaseSelectOption
            :label="
              hasPlaceholder ? placeholder : $t('common.default_placeholder')
            "
            :selected="null === selectedValue"
            @click="handleOptionClick(hide, null)"
          />
        </template>

        <BaseSelectOption
          v-for="option in filteredOptions"
          :key="option.value"
          :label="option.label"
          :value="option.value"
          :icon="option.icon"
          :extra-data="option.extraData"
          :selected="option.value === selectedValue"
          @click="handleOptionClick(hide, option)"
        >
          <template v-if="slots['option-content']" #base-option-content="{ optionLabel, optionValue, optionExtraData }">
            <slot name="option-content" :label="optionLabel" :value="optionValue" :extra-data="optionExtraData" />
          </template>
        </BaseSelectOption>
      </template>
    </BaseDropdown>

    <div class="show-until-desktop">
      <div class="mobile-select">
        <div class="mobile-select__trigger">
          <template v-if="slots['mobile-trigger']">
            <slot
              name="mobile-trigger"
              :placeholder-string="placeholderString"
              :show-mobile-select="showMobileSelect"
              :selected-option="selectedOption"
            />
          </template>

          <BaseSelectTrigger
            v-else
            :placeholder="placeholderString"
            :has-selected="hasSelected"
            :label="label"
            :theme="triggerTheme"
            @click="showMobileSelect"
          >
            <template v-if="slots['trigger-content']" #base-trigger-content>
              <slot
                name="trigger-content"
                :placeholder-string="placeholderString"
                :selected-option="selectedOption"
              />
            </template>
          </BaseSelectTrigger>
        </div>

        <div
          ref="mobileOverlay"
          class="mobile-select__overlay z-index__32"
          :class="{
            'mobile-select__overlay--opened': isOpenedMobile
          }"
          @click="handleClickOutside"
        >
          <div ref="mobileSelectModal" class="mobile-select-modal">
            <template v-if="search">
              <div class="select-search">
                <BaseInput v-model="searchValue" size="small" :placeholder="$t('common.select_search')" />
              </div>
            </template>

            <template v-if="includeDefaultOption">
              <BaseSelectOption
                :label="
                  hasPlaceholder
                    ? placeholder
                    : $t('common.default_placeholder')
                "
                :selected="null === selectedValue"
                mobile
                @click="handleMobileOptionClick(null)"
              />
            </template>

            <BaseSelectOption
              v-for="option in filteredOptions"
              :key="option.value"
              :label="option.label"
              :value="option.value"
              :icon="option.icon"
              :extra-data="option.extraData"
              :selected="option.value === selectedValue"
              mobile
              @click="handleMobileOptionClick(option)"
            >
              <template v-if="slots['option-content']" #base-option-content="{ optionLabel, optionValue, optionExtraData }">
                <slot name="option-content" :label="optionLabel" :value="optionValue" :extra-data="optionExtraData" />
              </template>
            </BaseSelectOption>
          </div>
        </div>
      </div>
    </div>

    <slot name="errors" />
  </div>
</template>

<script lang="ts" setup>
import type { SelectOption } from '~/types/common'
import BaseDropdown from '@/components/Base/BaseDropdown/BaseDropdown.vue'

type Props = {
  search?: boolean
  modelValue?: string | number | null
  selected?: string | number | null
  options: SelectOption[]
  label?: string
  triggerTheme?: 'default' | 'simple'
  placeholder?: string
  dropdownStyle?: { [key: string]: string }
  includeDefaultOption?: boolean
  width?: string
  dropdownWidth?: string
  error?: boolean
}

type Emits = {
  (e: 'update:modelValue', value: string | number | null): void,
  (e: 'select-option', value: SelectOption | null): void
}

const props = withDefaults(defineProps<Props>(), {
  search: false,
  modelValue: null,
  selected: null,
  label: '',
  dropdownStyle: () => ({}),
  triggerTheme: 'default',
  placeholder: '',
  width: '100%',
  dropdownWidth: '100%',
  includeDefaultOption: false,
  error: false
})
const emit = defineEmits<Emits>()
const { $device } = useNuxtApp()
const i18n = useI18n()
const dropdown = useTemplateRef<InstanceType<typeof BaseDropdown>>('dropdownRef')
const searchValue = shallowRef('')

const isOpenedMobile = shallowRef(false)
const mobileSelectModal = useTemplateRef<HTMLDivElement>('mobileSelectModal')

const {
  overflowShow,
  overflowHide
} = useOverflowed()

const slots = useSlots() as {
  'option-content'?: () => unknown
  'trigger-content'?: () => unknown
  'mobile-trigger'?: () => unknown
  'trigger'?: () => unknown
}

const filteredOptions = computed(() => {
  if (props.search && searchValue.value.length > 0) {
    return props.options.filter((item) => {
      return item.label.toLowerCase().includes(searchValue.value.toLowerCase())
    })
  } else {
    return props.options
  }
})

const model = computed({
  get: () => props.modelValue || props.selected,
  set: (value: string | number | null) => {
    emit('update:modelValue', value)
  }
})

const selectedValue = computed({
  get: () => props.modelValue || props.selected || '',
  set: (value: string | number) => {
    model.value = value
  }
})

const hasPlaceholder = computed(() => {
  return props.placeholder && props.placeholder.trim().length > 0
})

const selectedOption = computed(() => {
  return props.options.find((item) => {
    return item.value === selectedValue.value
  })
})

const hasSelected = computed(() => {
  return Boolean(selectedOption.value)
})

const placeholderString = computed(() => {
  if (selectedOption.value) {
    return selectedOption.value.label
  }

  if (hasPlaceholder.value) {
    return props.placeholder
  }

  return i18n.t('common.default_placeholder')
})

const handleOptionClick = (hide: () => void, option: SelectOption | null) => {
  hide()
  emit('update:modelValue', option?.value || null)
  emit('select-option', option)
}

const handleMobileOptionClick = (option: SelectOption | null) => {
  emit('update:modelValue', option?.value || null)
  emit('select-option', option)
  isOpenedMobile.value = false
  overflowShow()
}

const showMobileSelect = () => {
  isOpenedMobile.value = true
  overflowHide()
}

const handleClickOutside = (e: Event) => {
  if (!mobileSelectModal.value?.contains(e.target as HTMLElement)) {
    isOpenedMobile.value = false
    overflowShow()
  }
}

const onFocus = () => {
  if ($device.isDesktop) {
    dropdown.value!.show()
  } else {
    showMobileSelect()
  }
}

defineExpose({
  onFocus
})

</script>

<style lang="scss" scoped>
.select-wrapper {
  width: 100%;
}

.mobile-select {
  &__overlay {
    display: none;
    background-color: rgba(#000000, 0.6);
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    align-items: center;
    justify-content: center;

    &--opened {
      display: flex;
    }
  }
}

.mobile-select-modal {
  max-width: 90%;
  max-height: 90%;
  width: 100%;
  background-color: #fff;
  box-shadow: 0px 20px 40px rgba(0, 0, 0, 0.15);
  border-radius: 8px;
  overflow: auto;
}

.select-search {
  padding: 10px;
}
</style>
